import { MessageSquare, Users, Award, Zap, Mail, Instagram, Linkedin, Phone } from 'lucide-react';

const Community = () => {
  return (
    <div className="min-h-screen bg-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0C7779] rounded-2xl mb-6">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-6 text-[#0F172A]">
            Welcome to GrowOn Community
          </h1>
          <p className="text-xl text-[#64748B] max-w-4xl mx-auto">
            A student guidance community building a focused ecosystem from the ground up. 
            Clear direction, practical skills, and genuine connections.
          </p>
        </div>

        {/* Our Purpose */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="bg-white rounded-2xl border border-[#E2EFEA] p-8 mb-12">
            <h2 className="text-3xl font-bold mb-8 text-[#0F172A] text-center">Guidance Through Connection</h2>
            <p className="text-[#64748B] text-center mb-8">
              The right guidance at the right time can change a career trajectory. 
              We're a structured community focused on:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#3BC1A8]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#0C7779]">
                  <Users className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold mb-2 text-[#0F172A]">Students</h4>
                <p className="text-[#64748B] text-sm">Find direction and build practical skills</p>
              </div>
              
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#3BC1A8]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#0C7779]">
                  <Award className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold mb-2 text-[#0F172A]">Mentors</h4>
                <p className="text-[#64748B] text-sm">Share knowledge and directly impact futures</p>
              </div>
              
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#3BC1A8]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#0C7779]">
                  <Zap className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold mb-2 text-[#0F172A]">Everyone</h4>
                <p className="text-[#64748B] text-sm">Connect in a space focused on real growth</p>
              </div>
            </div>
          </div>

          {/* What We Are Building */}
          <h2 className="text-3xl font-bold mb-8 text-center text-[#0F172A]">What We Are Building</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-6 hover:border-[#249E94]/40 transition-all">
              <h3 className="text-lg font-bold mb-3 text-[#0F172A]">1. Direct Mentorship</h3>
              <p className="text-[#64748B] text-sm">Connect with industry professionals for honest conversations about careers and skills.</p>
            </div>
            
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-6 hover:border-[#249E94]/40 transition-all">
              <h3 className="text-lg font-bold mb-3 text-[#0F172A]">2. Practical Workshops</h3>
              <p className="text-[#64748B] text-sm">Hands-on sessions in Cybersecurity, AI/ML, Design, and Career Strategy.</p>
            </div>
            
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-6 hover:border-[#249E94]/40 transition-all">
              <h3 className="text-lg font-bold mb-3 text-[#0F172A]">3. Focused Network</h3>
              <p className="text-[#64748B] text-sm">A curated group serious about development. Quality over quantity.</p>
            </div>
            
            <div className="bg-white rounded-xl border border-[#E2EFEA] p-6 hover:border-[#249E94]/40 transition-all">
              <h3 className="text-lg font-bold mb-3 text-[#0F172A]">4. Project-Based Learning</h3>
              <p className="text-[#64748B] text-sm">Work on applicable projects, get feedback, and build real work portfolios.</p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white to-[#F4FAF9] rounded-2xl p-8 border border-[#E2EFEA]">
            <h2 className="text-2xl font-bold mb-6 text-[#0F172A] text-center">Join Us in Building This</h2>
            <p className="text-[#64748B] text-center mb-8">
              We're just getting started, and the community's shape will be defined by its earliest members. 
              If you believe in learning by doing and growing through sharing, you're in the right place.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <a 
                href="mailto:growoncommunity@gmail.com"
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-[#249E94] text-white rounded-lg hover:bg-[#1D867C] transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>growoncommunity@gmail.com</span>
              </a>
              
              <a 
                href="tel:+919767354578"
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-white text-[#0C7779] rounded-lg hover:bg-[#F4FAF9] transition-colors border border-[#E2EFEA]"
              >
                <Phone className="w-5 h-5" />
                <span>+91 9767354578</span>
              </a>
            </div>
            
            <div className="flex justify-center space-x-6">
              <a href="https://www.instagram.com/growon__community" target="_blank" rel="noopener noreferrer" className="text-[#64748B] hover:text-[#0C7779] transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
              <a href="https://www.linkedin.com/posts/nextgen-india9_official-announcement-we-would-like-to-activity-7411688145773170688-_TRV" target="_blank" rel="noopener noreferrer" className="text-[#64748B] hover:text-[#0C7779] transition-colors">
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;